import { useEffect, useRef } from 'react';
import { useFormApi, useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Box } from 'components/common/box';
import { DisplayField, Select } from 'components/common/form';
import { CheckboxInput } from 'components/common/form/common';

import type { GetCourseContextForAIRes } from 'shared-types/responses';
import type { FormValues } from './AIGenerateContentButton';

interface Props {
  chapterData: GetCourseContextForAIRes['dataByChapterId'];
  contextBlockIds: Record<string, true>;
  languageVersion: string;
  visible: boolean;
}

const AIGenerateContentChapterContext = ({
  chapterData,
  contextBlockIds,
  languageVersion,
  visible
}: Props) => {
  const formApi = useFormApi<FormValues>();
  const { values } = useFormState<FormValues>();

  const { messages, theme } = useReduxState();

  const blocksByType = useRef(null);

  useEffect(() => {
    getBlocks();
  }, []);

  useEffect(() => {
    if (visible) {
      formApi.setValue('chapterId', chapterIds[0] || 0);
      formApi.setValue('blockIds', contextBlockIds);
    }
  }, [visible]);

  const getBlocks = async () => {
    const { EditorBlockHeading } = await import(
      'components/editor/blocks/heading'
    );
    const { EditorBlockList } = await import('components/editor/blocks/list');
    const { EditorBlockQuote } = await import('components/editor/blocks/quote');
    const { EditorBlockText } = await import('components/editor/blocks/text');

    blocksByType.current = {
      heading: EditorBlockHeading,
      list: EditorBlockList,
      quote: EditorBlockQuote,
      text: EditorBlockText
    };
  };

  const handleChange = (blockId: number, isAdding: boolean) => {
    if (isAdding) {
      formApi.setValue('blockIds', { ...values.blockIds, [blockId]: true });
    } else {
      const blockIds = JSON.parse(JSON.stringify(values.blockIds));
      delete blockIds[blockId];

      formApi.setValue('blockIds', blockIds);
    }
  };

  const handleSelectAll = () => {
    let blockIds = JSON.parse(JSON.stringify(values.blockIds));
    if (allSelected) {
      blocks.forEach(({ id }) => {
        delete blockIds[id];
      });
    } else {
      blockIds = {
        ...blockIds,
        ...blocks.reduce(
          (blocks, block) => ({ ...blocks, [block.id]: true }),
          {}
        )
      };
    }
    formApi.setValue('blockIds', blockIds);
  };

  const chapterIds = Object.keys(chapterData || {});

  const blocks = chapterData?.[values.chapterId]?.blocks || [];

  const allSelected = blocks.every((block) => values.blockIds[block.id]);

  return (
    <div className="context-selection" style={{ height: '100%' }}>
      <div className="header">
        <Box padding={0}>
          <div className="header-title">
            {`${messages.commonContext}: ${
              Object.keys(values?.blockIds || {}).filter(
                (id) => values.blockIds[id]
              ).length
            } ${messages.editorHeaderBlocksSelected}`}
          </div>
          <div className="header-content">
            <div className="header-chapter-select">
              <Select
                field="chapterId"
                options={chapterIds.map((id) => ({
                  label: chapterData[id]?.title,
                  value: id
                }))}
                style={{ width: '100%' }}
              />
            </div>

            <div className="header-chapter-info">
              <div className="header-chapter-selected">
                {`${
                  blocks.filter((block) => values.blockIds[block.id]).length
                }/${blocks.length} ${messages.editorHeaderBlocksSelected}`}
              </div>

              <div className="header-chapter-select-all">
                <CheckboxInput
                  checked={allSelected}
                  id={`select-all-${chapterData?.id || 'empty'}`}
                  label={
                    allSelected
                      ? messages.commonClearAll
                      : messages.commonSelectAll
                  }
                  labelProps={{ style: { whiteSpace: 'nowrap' } }}
                  onChange={handleSelectAll}
                />
              </div>
            </div>
          </div>
        </Box>
      </div>

      <div className="blocks">
        <DisplayField field="blockIds" />

        {blocks.map((block) => {
          const BlockType = blocksByType?.current?.[block.type];

          return !BlockType ? null : (
            <div className="block" key={block.id}>
              <Box padding={0}>
                <div className="block-inner">
                  <BlockType
                    blockId={block.id}
                    data={block.draftData || block.data}
                    isPreview={true}
                    languageVersion={languageVersion}
                    type={block.type}
                    updateData={() => undefined}
                  />

                  <div className="block-select">
                    <CheckboxInput
                      checked={values.blockIds[block.id]}
                      id={`${block.id}`}
                      label={messages.commonSelect}
                      onChange={(ev) =>
                        handleChange(
                          block.id,
                          (ev.target as HTMLInputElement).checked
                        )
                      }
                      value={block.id}
                    />
                  </div>
                </div>
              </Box>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .context-selection {
          background-color: ${theme.lightGreyLighter};
          display: ${visible ? 'block' : 'none'};
          height: ${visible ? '100%' : 0};
          padding: 15px;
        }
        .header-title {
          padding: 10px 15px;
        }
        .header-content {
          align-items: flex-start;
          border: 1px solid ${theme.lightGrey};
          display: flex;
          flex-direction: column;
          gap: 15px;
          justify-content: space-between;
          padding: 10px 15px;
        }
        .header-chapter-select {
          flex-grow: 0;
          flex-shrink: 0;
          max-width: 100%;
          width: 400px;
        }
        .header-chapter-info {
          align-items: center;
          display: flex;
          flex-grow: 1;
          flex-shrink: 1;
          gap: 15px;
          justify-content: space-between;
        }
        .header-chapter-selected {
          color: ${theme.textColorLighter};
        }
        .header-chapter-select-all,
        .block-select {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.buttonBorderRadius};
          padding: 3px 6px;
        }
        .blocks {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: 15px;
        }
        .block-inner {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 400;
          padding: 15px 15px 50px;
          position: relative;
        }
        .block-select {
          position: absolute;
          right: 15px;
          bottom: 15px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .header-content {
            align-items: center;
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default AIGenerateContentChapterContext;
